import { useAllQueryParams } from "@/shared/lib/hooks";
import { type TSetState } from "@/shared/types/form.types";
import { BackButton } from "@/shared/ui";
import { ExportButton } from "@/widgets";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import { type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { TbSearch, TbPlus, TbFilter } from "react-icons/tb";
import styled, { keyframes } from "styled-components";

const slideIn = keyframes`
  from {
    transform: translateY(-10px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const HeaderContainer = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  padding: 10px;
  /* border-radius: 16px 16px 0 0; */
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  animation: slideIn 0.3s ease-out;
  
  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }
  
  .header-left {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
    min-width: 0;
  }
  
  .header-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }
  
  .title-section {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  
  .title {
    font-size: 1rem;
    font-weight: 700;
    color: #1e293b;
    background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0;
  }
  
  .search-container {
    position: relative;
    min-width: 100px;
    max-width: 200px;
    flex: 1;
    
    .search-input {
      background: white;
      border: 2px solid #e2e8f0;
      border-radius: 8px;
      padding: 8px 12px 8px 36px;
      font-size: 0.8rem;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      width: 100%;
      
      &:focus {
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        outline: none;
      }
      
      &::placeholder {
        color: #94a3b8;
      }
    }
    
    .search-icon {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #94a3b8;
      font-size: 1rem;
    }
  }
  
  .action-button {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border: none;
    border-radius: 12px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    color: white;
    
    &:hover {
      background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    }
    
    &:active {
      transform: translateY(0);
    }
    
    .button-icon {
      font-size: 1rem;
    }
  }
  
  @media (max-width: 768px) {
    padding: 16px;
    
    .header-content {
      flex-direction: column;
      align-items: stretch;
    }
    
    .header-left {
      flex-direction: column;
      align-items: stretch;
      gap: 12px;
    }
    
    .search-container {
      min-width: auto;
      max-width: none;
    }
    
    .title {
      font-size: 1.5rem;
    }
  }
`;


const TableHeader: FC<{
  searchable?: boolean;
  exportBtn?: boolean;
  setSearch: TSetState<string>;
  search: string | undefined;
  title?: ReactNode | string;
  headerChildren: ReactNode;
  headerChildrenSecondRow: ReactNode;
  deletable: boolean;

  selectedRows: any[];
  onDelete: () => void;
  onAddButton?: () => void;
  addButtonTitle?: string;
  dataUrl?: string;
  back?: boolean;
  manualBackButton?: ReactNode;
}> = ({
  onAddButton,
  //   @ts-expect-error any
  onDelete,

  setSearch,
  searchable,
  exportBtn,
  search,
  headerChildren,
  headerChildrenSecondRow,
  title,
  addButtonTitle,
  //   @ts-expect-error any
  dataUrl,
  back,
  manualBackButton,
}) => {
  const { t } = useTranslation();

  const allParams = useAllQueryParams();

  // const gradeArray = JSON.parse(allParams?.grade || "[]").map((grad: any) =>
  //   String(grad?._id)
  // );

  const grades = (grade: any) => {
    const parsed = typeof grade === "string" ? JSON.parse(grade) : grade;

    return Array.isArray(parsed)
      ? parsed.map((grad) => String(grad?._id))
      : parsed != null
        ? [String(parsed)]
        : [];
  };

  const gradeArray = grades(allParams?.grade);

  const queryParts = [
    gradeArray && gradeArray?.length > 0
      ? `grade=[${gradeArray.join(",")}]`
      : "",
    allParams?.date_from
      ? `date_from=${encodeURIComponent(allParams.date_from)}`
      : "",
    allParams?.date_to ? `date_to=${encodeURIComponent(allParams.date_to)}` : "",
    allParams?.showDebitors
      ? `showDebitors=${encodeURIComponent(allParams.showDebitors)}`
      : "",
    allParams?.state ? `state=${encodeURIComponent(allParams.state)}` : "",
    allParams?.hasContract
      ? `hasContract=${encodeURIComponent(allParams.hasContract)}`
      : "",
    allParams?.search ? `search=${encodeURIComponent(allParams.search)}` : "",
    allParams?.hasAboniment !== undefined
      ? `hasAboniment=${encodeURIComponent(allParams.hasAboniment)}`
      : "",
    allParams?.abonimentId
      ? `abonimentId=${encodeURIComponent(allParams.abonimentId)}`
      : "",
    allParams?.statusId
      ? `statusId=${encodeURIComponent(allParams.statusId)}`
      : "",
    allParams?.language
      ? `language=${encodeURIComponent(allParams.language)}`
      : "",
  ];

  // Join non-empty parts with "&"
  const exportUrl = `/student/export?${queryParts.filter(Boolean).join("&")}`;

  return (
    <HeaderContainer>
      <div className="header-content">
        <div className="header-left">
          <div className="title-section">
            {back && <BackButton />}
            {manualBackButton}
            <h2 className="title">{title}</h2>
          </div>
          
          {searchable && (
            <div className="search-container">
              <TbSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder={"Qidirish..."}
                value={search || ""}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
              />
            </div>
          )}
        </div>
        
        <div className="header-right">
          {exportBtn && <ExportButton url={exportUrl} />}
          <div>{headerChildren}</div>
          {onAddButton && (
            <button
              className="action-button"
              onClick={onAddButton}
            >
              <TbPlus className="button-icon" />
              <span>{addButtonTitle || t("general.add") || "Add"}</span>
            </button>
          )}
        </div>
      </div>
      
      {headerChildrenSecondRow && (
        <div >
          {headerChildrenSecondRow}
        </div>
      )}
    </HeaderContainer>
  );
};

export default TableHeader;
